import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { ArrowDownToLine, ArrowUpFromLine, Copy, Mail, ArrowLeftRight, Wallet as WalletIcon, TrendingUp, Zap, Repeat, ShieldCheck, Trash2, Coins, Fingerprint, Send } from 'lucide-react';
import DepositDialog from '@/components/DepositDialog';
import PendingWithdrawalsLive from '@/components/PendingWithdrawalsLive';
import SendDialog from '@/components/SendDialog';

// Per-coin brand accent colors — powers the tinted icon backdrop on wallet rows
const COIN_COLORS = {
  BTC: '#F7931A', ETH: '#627EEA', USDT: '#26A17B', BNB: '#F3BA2F',
  SOL: '#9945FF', XRP: '#00AAE4', ADA: '#0033AD', DOGE: '#C2A633',
  MATIC: '#8247E5', DOT: '#E6007A', TRX: '#FF060A', LINK: '#2A5ADA',
  LTC: '#345D9D', AVAX: '#E84142', ATOM: '#2E3148', NEAR: '#00C08B',
  SHIB: '#FFA409', UNI: '#FF007A', XLM: '#7D00FF', ARB: '#28A0F0',
};
const coinColor = (sym) => COIN_COLORS[String(sym || '').toUpperCase()] || '#848E9C';

export default function Wallet() {
  const { user, refresh } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [allWallets, setAllWallets] = useState(null);
  const [txs, setTxs] = useState([]);
  const [depositOpen, setDepositOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [addrOpen, setAddrOpen] = useState(false);
  const [wSymbol, setWSymbol] = useState('USDT');
  const [wAmount, setWAmount] = useState('');
  const [wAddr, setWAddr] = useState('');
  const [digestBusy, setDigestBusy] = useState(false);
  // Transfer form
  const [tFrom, setTFrom] = useState('spot');
  const [tTo, setTTo] = useState('futures');
  const [tSymbol, setTSymbol] = useState('USDT');
  const [tAmount, setTAmount] = useState('');
  const [tBusy, setTBusy] = useState(false);
  // Convert form
  const [cWallet, setCWallet] = useState('spot');
  const [cFrom, setCFrom] = useState('USDT');
  const [cTo, setCTo] = useState('BTC');
  const [cAmount, setCAmount] = useState('');
  const [cBusy, setCBusy] = useState(false);
  const [cRate, setCRate] = useState(null);
  // Whitelist address manager
  const [addresses, setAddresses] = useState([]);
  const [addrSymbol, setAddrSymbol] = useState('USDT');
  const [addrLabel, setAddrLabel] = useState('');
  const [addrValue, setAddrValue] = useState('');
  const [addrBusy, setAddrBusy] = useState(false);
  // Email verification step
  const [verifyStep, setVerifyStep] = useState(null); // { pending_id, email_masked, mock_code }
  const [verifyCode, setVerifyCode] = useState('');
  const [prefsBusy, setPrefsBusy] = useState(false);
  // 30-day PnL history for Overview chart
  const [pnlHistory, setPnlHistory] = useState(null);
  const [pnlDays, setPnlDays] = useState(30);
  const [compareSym, setCompareSym] = useState(''); // e.g. 'SOL', 'PAXG', ''=off
  const [compareOptions, setCompareOptions] = useState([]);
  // 2FA + Sessions
  const [twoFa, setTwoFa] = useState(null); // { enabled }
  const [sessions, setSessions] = useState([]);
  const [twoFaOpen, setTwoFaOpen] = useState(false);
  const [twoFaSetup, setTwoFaSetup] = useState(null); // { secret, otpauth_uri }
  const [twoFaCode, setTwoFaCode] = useState('');
  const [twoFaBusy, setTwoFaBusy] = useState(false);
  // Withdraw TOTP code
  const [wTotp, setWTotp] = useState('');

  const digestOn = user?.email_digest_optin !== false;
  const toggleDigest = async () => {
    setDigestBusy(true);
    try {
      const next = !digestOn;
      await api.post('/digest/settings', { email_digest_optin: next });
      if (refresh) await refresh();
      toast.success(next ? 'Weekly digest ON' : 'Unsubscribed from weekly digest');
    } catch (e) { toast.error('Failed to update'); }
    finally { setDigestBusy(false); }
  };

  const load = async () => {
    const pnlUrl = `/wallet/pnl_history?days=${pnlDays}${compareSym ? `&compare=${encodeURIComponent(compareSym)}` : ''}`;
    const [w, aw, t, a, pnl, twofa, sess, opts] = await Promise.all([
      api.get('/wallet'),
      api.get('/wallet/all'),
      api.get('/wallet/transactions'),
      api.get('/wallet/withdraw_addresses').catch(() => ({ data: { addresses: [] } })),
      api.get(pnlUrl).catch(() => ({ data: null })),
      api.get('/auth/2fa/status').catch(() => ({ data: { enabled: false } })),
      api.get('/auth/sessions').catch(() => ({ data: { sessions: [] } })),
      api.get('/wallet/compare_options').catch(() => ({ data: { options: [] } })),
    ]);
    setWallet(w.data);
    setAllWallets(aw.data);
    setTxs(t.data.transactions);
    setAddresses(a.data.addresses || []);
    setPnlHistory(pnl.data || null);
    setTwoFa(twofa.data || { enabled: false });
    setSessions(sess.data?.sessions || []);
    setCompareOptions(opts.data?.options || []);
  };
  useEffect(() => { load(); }, [pnlDays, compareSym]);

  const withdraw = async () => {
    try {
      const body = { symbol: wSymbol, amount: parseFloat(wAmount), address: wAddr };
      if (wTotp) body.totp_code = wTotp.replace(/\s+/g, '');
      await api.post('/wallet/withdraw', body);
      toast.success('Withdrawal submitted — awaiting admin approval');
      setWithdrawOpen(false); setWAmount(''); setWAddr(''); setWTotp('');
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === 'object' && d.require_2fa_setup) {
        toast.error('Enable 2FA in Preferences to withdraw');
        setWithdrawOpen(false); setTwoFaOpen(true);
      } else if (d && typeof d === 'object' && d.require_kyc) {
        toast.error(d.message || 'KYC required for further withdrawals');
        setWithdrawOpen(false);
        // Small delay so the toast is visible before we navigate
        setTimeout(() => { window.location.href = '/kyc'; }, 1200);
      } else if (d && typeof d === 'object' && d.require_2fa) {
        toast.error(d.message || '2FA code required');
      } else {
        toast.error((typeof d === 'string' ? d : null) || 'Withdrawal failed');
      }
    }
  };

  // ---- 2FA helpers ----
  const startTwoFaSetup = async () => {
    setTwoFaBusy(true);
    try {
      const r = await api.post('/auth/2fa/setup');
      setTwoFaSetup(r.data);
      setTwoFaCode('');
      setTwoFaOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to start setup'); }
    finally { setTwoFaBusy(false); }
  };
  const confirmTwoFa = async () => {
    const code = twoFaCode.replace(/\s+/g, '');
    if (code.length < 6) { toast.error('Enter the 6-digit code'); return; }
    setTwoFaBusy(true);
    try {
      await api.post('/auth/2fa/enable', { code });
      toast.success('2FA enabled — withdrawals now protected');
      setTwoFaOpen(false); setTwoFaSetup(null); setTwoFaCode('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid code'); }
    finally { setTwoFaBusy(false); }
  };
  const disableTwoFa = async () => {
    const code = twoFaCode.replace(/\s+/g, '');
    if (code.length < 6) { toast.error('Enter your 6-digit code to disable'); return; }
    setTwoFaBusy(true);
    try {
      await api.post('/auth/2fa/disable', { code });
      toast.success('2FA disabled');
      setTwoFaOpen(false); setTwoFaCode('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid code'); }
    finally { setTwoFaBusy(false); }
  };
  const revokeSession = async (sid) => {
    try {
      await api.delete(`/auth/sessions/${sid}`);
      toast.success('Session revoked');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to revoke'); }
  };
  const transfer = async () => {
    const amt = parseFloat(tAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setTBusy(true);
    try {
      await api.post('/wallet/transfer', { from: tFrom, to: tTo, symbol: tSymbol, amount: amt });
      toast.success(`Transferred ${amt} ${tSymbol}: ${tFrom} → ${tTo}`);
      setTransferOpen(false); setTAmount('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Transfer failed'); }
    finally { setTBusy(false); }
  };

  const convert = async () => {
    const amt = parseFloat(cAmount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    setCBusy(true);
    try {
      const r = await api.post('/wallet/convert', {
        from_symbol: cFrom, to_symbol: cTo, amount: amt, wallet: cWallet,
      });
      toast.success(`Converted ${amt} ${cFrom} → ${r.data.to_amount.toFixed(6)} ${cTo}`);
      setConvertOpen(false); setCAmount(''); setCRate(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Convert failed'); }
    finally { setCBusy(false); }
  };

  const addAddress = async () => {
    if (!addrLabel.trim() || !addrValue.trim()) { toast.error('Label and address are required'); return; }
    setAddrBusy(true);
    try {
      const r = await api.post('/wallet/withdraw_addresses', {
        symbol: addrSymbol, label: addrLabel, address: addrValue,
      });
      setVerifyStep({
        pending_id: r.data.pending_id,
        email_masked: r.data.email_masked,
        mock_code: r.data.mock_code,
      });
      toast.success(`Code sent to ${r.data.email_masked} — enter it below to verify`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add'); }
    finally { setAddrBusy(false); }
  };
  const verifyAddress = async () => {
    if (!verifyStep?.pending_id || !verifyCode.trim()) { toast.error('Enter the code from your email'); return; }
    setAddrBusy(true);
    try {
      await api.post('/wallet/withdraw_addresses/verify', { pending_id: verifyStep.pending_id, code: verifyCode.trim() });
      toast.success('Address verified & whitelisted (24h cooldown starts now)');
      setVerifyStep(null); setVerifyCode(''); setAddrLabel(''); setAddrValue('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Verification failed'); }
    finally { setAddrBusy(false); }
  };
  const cancelVerify = () => { setVerifyStep(null); setVerifyCode(''); };
  const removeAddress = async (addr_id) => {
    try {
      await api.delete(`/wallet/withdraw_addresses/${addr_id}`);
      toast.success('Removed');
      load();
    } catch (e) { toast.error('Failed to remove'); }
  };
  const togglePref = async (key) => {
    if (!allWallets?.prefs) return;
    setPrefsBusy(true);
    try {
      const next = !allWallets.prefs[key];
      await api.post('/wallet/preferences', { [key]: next });
      toast.success(`${key === 'auto_transfer_to_futures' ? 'Auto-transfer' : 'Whitelist-only'} ${next ? 'ON' : 'OFF'}`);
      load();
    } catch (e) { toast.error('Failed'); }
    finally { setPrefsBusy(false); }
  };

  const assets = ['USDT','BTC','ETH','BNB','SOL','XRP','ADA','DOGE','MATIC','DOT'];

  return (
    <div className="max-w-[1440px] mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-white text-4xl font-bold tracking-tighter">Wallet</h1>
          <p className="text-[#848E9C] text-sm mt-1">Manage your demo balances and transactions</p>
        </div>
        <div className="flex gap-3">
          <DepositDialog open={depositOpen} onOpenChange={setDepositOpen} />
          <Button
            onClick={() => setSendOpen(true)}
            variant="outline"
            className="border-[#2B3139] bg-[#181A20] text-[#0ECB81] hover:bg-[#0ECB81]/10 hover:border-[#0ECB81]/50 rounded-sm"
            data-testid="open-send-btn"
          >
            <Send className="w-4 h-4 mr-1.5" /> Send
          </Button>
          <SendDialog open={sendOpen} onOpenChange={setSendOpen}
            spotBalances={Object.fromEntries((wallet?.balances || []).map(b => [String(b.symbol).toUpperCase(), Number(b.amount || 0)]))}
            onSent={() => { load(); }} />
          <Button
            onClick={() => setTransferOpen(true)}
            variant="outline"
            className="border-[#2B3139] bg-[#181A20] text-white hover:bg-[#2B3139] rounded-sm"
            data-testid="open-transfer-btn"
          >
            <ArrowLeftRight className="w-4 h-4 mr-1.5" /> Transfer
          </Button>
          <Link
            to="/convert"
            className="inline-flex items-center gap-1.5 border border-[#2B3139] bg-[#181A20] text-white hover:bg-[#2B3139] rounded-sm px-3 py-2 text-sm font-medium h-9"
            data-testid="open-convert-btn"
          >
            <Repeat className="w-4 h-4" /> Convert
          </Link>
          <Button
            onClick={() => setAddrOpen(true)}
            variant="outline"
            className="border-[#2B3139] bg-[#181A20] text-white hover:bg-[#2B3139] rounded-sm"
            data-testid="open-whitelist-btn"
          >
            <ShieldCheck className="w-4 h-4 mr-1.5" /> Addresses
          </Button>
          <Dialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" className="border-[#2B3139] bg-[#181A20] text-white hover:bg-[#2B3139] rounded-sm" data-testid="open-withdraw-btn">
                <ArrowUpFromLine className="w-4 h-4 mr-1.5" /> Withdraw
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm">
              <DialogHeader><DialogTitle>Withdraw</DialogTitle></DialogHeader>
              {user?.kyc_status !== 'verified' ? (
                <div className="text-sm text-[#F6465D] p-3 border border-[#F6465D]/30 bg-[#F6465D]/10 rounded-sm">
                  KYC verification required to withdraw. Complete KYC to unlock withdrawals.
                </div>
              ) : (
                <div className="space-y-3">
                  <Select value={wSymbol} onValueChange={setWSymbol}>
                    <SelectTrigger className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#181A20] border-[#2B3139] text-white">
                      {assets.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input placeholder="Amount" value={wAmount} onChange={e=>setWAmount(e.target.value)} type="number"
                    className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono" />
                  <Input placeholder="Destination address" value={wAddr} onChange={e=>setWAddr(e.target.value)}
                    className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono" />
                  {allWallets?.prefs && allWallets.prefs.kyc_status !== 'verified' && (
                    <div className="border border-[#F0B90B]/40 bg-[#F0B90B]/5 p-3 rounded-sm text-[11px] text-[#F0B90B] space-y-0.5" data-testid="withdraw-kyc-cap-block">
                      <div className="flex items-center gap-1.5 font-semibold uppercase">
                        <ShieldCheck className="w-3 h-3" /> Non-KYC withdrawal cap
                      </div>
                      <div className="text-[#B7BDC6]">
                        Remaining allowance: <span className="text-[#F0B90B] font-mono">${(allWallets.prefs.remaining_non_kyc_usd ?? 0).toLocaleString()}</span> of <span className="font-mono">${(allWallets.prefs.kyc_threshold_usd ?? 50000).toLocaleString()}</span>
                      </div>
                      <div className="text-[#5E6673]">Complete <a href="/kyc" className="underline text-[#FCD535]">KYC</a> to remove the cap entirely.</div>
                    </div>
                  )}
                  {twoFa?.enabled ? (
                    <div className="border border-[#FCD535]/40 bg-[#FCD535]/5 p-3 rounded-sm space-y-1" data-testid="withdraw-totp-block">
                      <div className="text-[10px] uppercase font-mono text-[#FCD535]">2FA code required</div>
                      <Input placeholder="123 456" value={wTotp}
                        onChange={e=>setWTotp(e.target.value.replace(/[^0-9]/g,''))}
                        inputMode="numeric" maxLength={6}
                        className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono text-lg tracking-widest text-center"
                        data-testid="withdraw-totp-input" />
                    </div>
                  ) : (
                    <div className="border border-[#F6465D]/40 bg-[#F6465D]/10 p-3 rounded-sm text-[11px] text-[#F6465D]" data-testid="withdraw-2fa-warning">
                      2FA is mandatory for withdrawals. Set it up first in Security & Devices below.
                    </div>
                  )}
                  <Button onClick={withdraw} disabled={twoFa?.enabled && wTotp.length < 6}
                    className="w-full bg-[#F6465D] hover:bg-[#E33A50] text-white rounded-sm disabled:opacity-50"
                    data-testid="confirm-withdraw-btn">Confirm Withdrawal</Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Total balance across all sub-wallets */}
      <div className="border border-[#2B3139] bg-gradient-to-br from-[#181A20] to-[#0B0E11] p-8 mb-6">
        <div className="text-xs uppercase tracking-widest text-[#848E9C]">Est. Total Value</div>
        <div className="font-mono text-white text-5xl font-medium tracking-tight mt-2" data-testid="total-portfolio">
          ${(allWallets?.total_usd ?? wallet?.total_usd ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
        </div>
        {allWallets && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-5" data-testid="wallet-summary-cards">
            <div className="border border-[#2B3139] bg-[#0B0E11] p-3">
              <div className="text-[10px] uppercase font-mono text-[#848E9C] flex items-center gap-1">
                <WalletIcon className="w-3 h-3" /> Funding
              </div>
              <div className="text-white font-mono text-lg mt-1" data-testid="summary-funding">
                ${allWallets.funding.total_usd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </div>
              <div className="text-[10px] text-[#5E6673]">Deposit source</div>
            </div>
            <div className="border border-[#2B3139] bg-[#0B0E11] p-3">
              <div className="text-[10px] uppercase font-mono text-[#848E9C] flex items-center gap-1">
                <TrendingUp className="w-3 h-3" /> Spot
              </div>
              <div className="text-white font-mono text-lg mt-1" data-testid="summary-spot">
                ${allWallets.spot.total_usd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </div>
              <div className="text-[10px] text-[#5E6673]">Trading balances</div>
            </div>
            <div className="border border-[#2B3139] bg-[#0B0E11] p-3">
              <div className="text-[10px] uppercase font-mono text-[#848E9C] flex items-center gap-1">
                <Zap className="w-3 h-3" /> Futures USDT-M
              </div>
              <div className="text-white font-mono text-lg mt-1" data-testid="summary-futures">
                ${allWallets.futures.equity_usdt.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </div>
              <div className={`text-[10px] font-mono ${allWallets.futures.unrealized_pnl_usdt >= 0 ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>
                UPnL {allWallets.futures.unrealized_pnl_usdt >= 0 ? '+' : ''}${allWallets.futures.unrealized_pnl_usdt}
              </div>
            </div>
            <div className="border border-[#2B3139] bg-[#0B0E11] p-3">
              <div className="text-[10px] uppercase font-mono text-[#848E9C] flex items-center gap-1">
                <Coins className="w-3 h-3" /> Futures COIN-M
              </div>
              <div className="text-white font-mono text-lg mt-1" data-testid="summary-coin-m">
                ${(allWallets.coin_m?.total_usd || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </div>
              <div className="text-[10px] text-[#5E6673]">BTC/ETH collateral</div>
            </div>
          </div>
        )}
      </div>

      {/* LIVE: pending-withdrawals indicator (only rendered when queue is non-empty) */}
      <div className="mb-6">
        <PendingWithdrawalsLive />
      </div>

      {/* Weekly digest opt-in */}
      <div className="border border-[#2B3139] bg-[#181A20] px-5 py-3 mb-6 flex flex-wrap items-center gap-3" data-testid="digest-optin-card">
        <Mail className={`w-4 h-4 ${digestOn ? 'text-[#FCD535]' : 'text-[#5E6673]'}`} />
        <div className="flex-1 min-w-[220px]">
          <div className="text-white text-sm font-semibold">Weekly digest email</div>
          <div className="text-[11px] text-[#848E9C]">Sunday 09:00 UTC — PnL, referrals, battle wins, staking rewards</div>
        </div>
        <button
          onClick={toggleDigest}
          disabled={digestBusy}
          className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${digestOn ? 'bg-[#FCD535]' : 'bg-[#2B3139]'} disabled:opacity-50`}
          data-testid="digest-optin-toggle"
          aria-label={digestOn ? 'Unsubscribe' : 'Subscribe'}
        >
          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${digestOn ? 'translate-x-4' : 'translate-x-0.5'}`} />
        </button>
        <span className={`text-[10px] uppercase font-mono ${digestOn ? 'text-[#FCD535]' : 'text-[#848E9C]'}`}>{digestOn ? 'ON' : 'OFF'}</span>
      </div>

      {/* Preferences: auto-transfer + whitelist-only withdrawals + convert alerts */}
      {allWallets?.prefs && (
        <div className="border border-[#2B3139] bg-[#181A20] px-5 py-3 mb-6 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="wallet-prefs-card">
          <div className="flex items-center gap-3">
            <ArrowLeftRight className={`w-4 h-4 ${allWallets.prefs.auto_transfer_to_futures ? 'text-[#FCD535]' : 'text-[#5E6673]'}`} />
            <div className="flex-1">
              <div className="text-white text-sm font-semibold">Auto-transfer on trade</div>
              <div className="text-[11px] text-[#848E9C]">Pull USDT from Spot into Futures when a position needs margin</div>
            </div>
            <button
              onClick={() => togglePref('auto_transfer_to_futures')}
              disabled={prefsBusy}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${allWallets.prefs.auto_transfer_to_futures ? 'bg-[#FCD535]' : 'bg-[#2B3139]'} disabled:opacity-50`}
              data-testid="auto-transfer-toggle"
              aria-label="Toggle auto-transfer"
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${allWallets.prefs.auto_transfer_to_futures ? 'translate-x-4' : 'translate-x-0.5'}`} />
            </button>
          </div>
          <div className="flex items-center gap-3">
            <ShieldCheck className={`w-4 h-4 ${allWallets.prefs.whitelist_only_withdrawals ? 'text-[#0ECB81]' : 'text-[#5E6673]'}`} />
            <div className="flex-1">
              <div className="text-white text-sm font-semibold">Whitelist-only withdrawals</div>
              <div className="text-[11px] text-[#848E9C]">Only allow withdrawals to addresses you have saved</div>
            </div>
            <button
              onClick={() => togglePref('whitelist_only_withdrawals')}
              disabled={prefsBusy}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${allWallets.prefs.whitelist_only_withdrawals ? 'bg-[#0ECB81]' : 'bg-[#2B3139]'} disabled:opacity-50`}
              data-testid="whitelist-only-toggle"
              aria-label="Toggle whitelist-only"
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${allWallets.prefs.whitelist_only_withdrawals ? 'translate-x-4' : 'translate-x-0.5'}`} />
            </button>
          </div>
          <div className="flex items-center gap-3">
            <Repeat className={`w-4 h-4 ${allWallets.prefs.convert_alerts_optin ? 'text-[#FCD535]' : 'text-[#5E6673]'}`} />
            <div className="flex-1">
              <div className="text-white text-sm font-semibold">Convert alerts</div>
              <div className="text-[11px] text-[#848E9C]">Push + email when a limit fills or a DCA gets skipped</div>
            </div>
            <button
              onClick={() => togglePref('convert_alerts_optin')}
              disabled={prefsBusy}
              className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${allWallets.prefs.convert_alerts_optin ? 'bg-[#FCD535]' : 'bg-[#2B3139]'} disabled:opacity-50`}
              data-testid="convert-alerts-toggle"
              aria-label="Toggle convert alerts"
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${allWallets.prefs.convert_alerts_optin ? 'translate-x-4' : 'translate-x-0.5'}`} />
            </button>
          </div>
        </div>
      )}

      {/* Anti-phishing code — appears in every convert/withdrawal email */}
      {allWallets?.prefs && (
        <AntiPhishingRow current={allWallets.prefs.anti_phishing_code || ''} onSave={async (v) => {
          try {
            await api.post('/wallet/preferences', { anti_phishing_code: v });
            toast.success(v ? 'Anti-phishing phrase saved' : 'Anti-phishing phrase cleared');
            load();
          } catch (e) { toast.error('Failed'); }
        }} />
      )}

      {/* Security & Devices: 2FA + Active Sessions */}
      <SecurityDevicesCard
        twoFa={twoFa}
        sessions={sessions}
        onSetup={startTwoFaSetup}
        onDisable={() => { setTwoFaSetup(null); setTwoFaCode(''); setTwoFaOpen(true); }}
        onRevoke={revokeSession}
      />

      {/* 2FA setup / disable dialog */}
      <Dialog open={twoFaOpen} onOpenChange={setTwoFaOpen}>
        <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm max-w-md" data-testid="twofa-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-[#FCD535]" />
              {twoFa?.enabled && !twoFaSetup ? 'Disable 2FA' : 'Enable 2FA (Google Authenticator)'}
            </DialogTitle>
          </DialogHeader>
          {/* Setup path (secret + QR) */}
          {twoFaSetup && !twoFa?.enabled && (
            <div className="space-y-3">
              <div className="text-xs text-[#B7BDC6]">
                Scan the QR in Google Authenticator (or any TOTP app), then enter the current 6-digit code to activate.
              </div>
              <div className="grid place-items-center bg-white p-3 rounded-sm">
                <img
                  alt="2FA QR code"
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(twoFaSetup.otpauth_uri)}`}
                  className="w-40 h-40"
                  data-testid="twofa-qr"
                />
              </div>
              <div className="border border-[#2B3139] bg-[#0B0E11] p-2 rounded-sm">
                <div className="text-[10px] uppercase font-mono text-[#848E9C]">Manual secret</div>
                <div className="font-mono text-[13px] text-[#FCD535] break-all select-all" data-testid="twofa-secret">
                  {twoFaSetup.secret}
                </div>
              </div>
              <Input placeholder="Enter 6-digit code" value={twoFaCode}
                onChange={e=>setTwoFaCode(e.target.value.replace(/[^0-9]/g,''))}
                inputMode="numeric" maxLength={6}
                className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono text-lg tracking-widest text-center"
                data-testid="twofa-code-input" />
              <Button onClick={confirmTwoFa} disabled={twoFaBusy || twoFaCode.length < 6}
                className="w-full bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold"
                data-testid="twofa-confirm-btn">
                {twoFaBusy ? 'Enabling…' : 'Enable 2FA'}
              </Button>
            </div>
          )}
          {/* Disable path (requires current code) */}
          {twoFa?.enabled && !twoFaSetup && (
            <div className="space-y-3">
              <div className="text-xs text-[#F6465D] p-2 border border-[#F6465D]/30 bg-[#F6465D]/10 rounded-sm">
                Disabling 2FA will block future withdrawals until you re-enable it (2FA is mandatory for withdrawals).
              </div>
              <Input placeholder="Current 6-digit code" value={twoFaCode}
                onChange={e=>setTwoFaCode(e.target.value.replace(/[^0-9]/g,''))}
                inputMode="numeric" maxLength={6}
                className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono text-lg tracking-widest text-center"
                data-testid="twofa-disable-code" />
              <Button onClick={disableTwoFa} disabled={twoFaBusy || twoFaCode.length < 6}
                className="w-full bg-[#F6465D] hover:bg-[#E33A50] text-white rounded-sm font-semibold"
                data-testid="twofa-disable-btn">
                {twoFaBusy ? 'Disabling…' : 'Disable 2FA'}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Sub-wallets tabbed view */}
      <Tabs defaultValue="overview" className="mb-8">
        <TabsList className="bg-[#181A20] border border-[#2B3139] rounded-sm">
          <TabsTrigger value="overview" className="data-[state=active]:bg-[#FCD535] data-[state=active]:text-[#0B0E11] rounded-none px-4" data-testid="wallet-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="funding" className="data-[state=active]:bg-[#FCD535] data-[state=active]:text-[#0B0E11] rounded-none px-4" data-testid="wallet-tab-funding">Funding</TabsTrigger>
          <TabsTrigger value="spot" className="data-[state=active]:bg-[#FCD535] data-[state=active]:text-[#0B0E11] rounded-none px-4" data-testid="wallet-tab-spot">Spot</TabsTrigger>
          <TabsTrigger value="futures" className="data-[state=active]:bg-[#FCD535] data-[state=active]:text-[#0B0E11] rounded-none px-4" data-testid="wallet-tab-futures">Futures USDT-M</TabsTrigger>
          <TabsTrigger value="coin_m" className="data-[state=active]:bg-[#FCD535] data-[state=active]:text-[#0B0E11] rounded-none px-4" data-testid="wallet-tab-coin-m">Futures COIN-M</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="flex items-center gap-2 mt-3 flex-wrap" data-testid="pnl-range-chips">
            <span className="text-[10px] uppercase font-mono text-[#848E9C]">Range:</span>
            <div className="inline-flex bg-[#181A20] border border-[#2B3139] rounded-sm">
              {[
                { d: 7, label: '7D' },
                { d: 30, label: '30D' },
                { d: 90, label: '90D' },
                { d: 365, label: '1Y' },
              ].map(r => (
                <button
                  key={r.d}
                  onClick={() => setPnlDays(r.d)}
                  className={`px-3 py-1 text-xs uppercase font-mono ${pnlDays === r.d ? 'bg-[#FCD535] text-[#0B0E11]' : 'text-[#848E9C] hover:text-white'}`}
                  data-testid={`pnl-range-${r.d}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <span className="text-[10px] uppercase font-mono text-[#848E9C] ml-3">Compare vs:</span>
            <select
              value={compareSym}
              onChange={e => setCompareSym(e.target.value)}
              className="bg-[#0B0E11] border border-[#2B3139] text-white font-mono text-xs px-2 py-1 rounded-sm focus:border-[#FCD535] outline-none"
              data-testid="pnl-compare-select"
            >
              <option value="">— None —</option>
              {compareOptions.filter(o => o.symbol !== 'BTC' && o.symbol !== 'ETH').map(o => (
                <option key={o.symbol} value={o.symbol}>{o.label}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="or symbol (e.g. LINK)"
              maxLength={12}
              value={compareSym && !compareOptions.find(o => o.symbol === compareSym) ? compareSym : ''}
              onChange={e => setCompareSym(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
              className="bg-[#0B0E11] border border-[#2B3139] text-white font-mono text-xs px-2 py-1 rounded-sm w-40 focus:border-[#FCD535] outline-none"
              data-testid="pnl-compare-custom-input"
            />
            {compareSym && (
              <button
                onClick={() => setCompareSym('')}
                className="text-[10px] uppercase font-mono text-[#848E9C] hover:text-[#F6465D] px-1"
                data-testid="pnl-compare-clear"
              >Clear</button>
            )}
          </div>
          <PnlHistoryChart pnl={pnlHistory} />
          <BalancesTable rows={wallet?.balances || []} testId="balances-table" emptyLabel="No balances yet" title="All Spot Balances" />
        </TabsContent>
        <TabsContent value="funding">
          <BalancesTable rows={allWallets?.funding?.balances || []} testId="funding-balances-table" emptyLabel="Funding wallet is empty — use Transfer to move USDT here for future withdrawals." title="Funding Balances" />
        </TabsContent>
        <TabsContent value="spot">
          <BalancesTable rows={allWallets?.spot?.balances || wallet?.balances || []} testId="spot-balances-table" emptyLabel="No spot balances." title="Spot Balances" />
        </TabsContent>
        <TabsContent value="futures">
          {allWallets && (
            <div className="border border-[#2B3139] bg-[#181A20] p-4 mt-3 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="futures-wallet-stats">
              <div>
                <div className="text-[10px] uppercase font-mono text-[#848E9C]">Available USDT</div>
                <div className="text-white font-mono text-lg mt-0.5">${allWallets.futures.available_usdt.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-mono text-[#848E9C]">Margin at Risk</div>
                <div className="text-[#FCD535] font-mono text-lg mt-0.5">${allWallets.futures.margin_at_risk_usdt.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-mono text-[#848E9C]">Unrealized PnL</div>
                <div className={`font-mono text-lg mt-0.5 ${allWallets.futures.unrealized_pnl_usdt >= 0 ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`}>
                  {allWallets.futures.unrealized_pnl_usdt >= 0 ? '+' : ''}${allWallets.futures.unrealized_pnl_usdt}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-mono text-[#848E9C]">Open Positions</div>
                <div className="text-white font-mono text-lg mt-0.5">{allWallets.futures.open_positions}</div>
              </div>
            </div>
          )}
          <BalancesTable rows={allWallets?.futures?.balances || []} testId="futures-balances-table" emptyLabel="No USDT in Futures — use Transfer to move USDT from Spot to open positions faster." title="Futures USDT-M" />
        </TabsContent>
        <TabsContent value="coin_m">
          <div className="border border-[#2B3139] bg-[#181A20] px-4 py-3 mt-3 text-[11px] text-[#848E9C] flex items-center gap-2" data-testid="coin-m-note">
            <Coins className="w-3.5 h-3.5 text-[#FCD535]" />
            COIN-M perpetuals settle in the underlying crypto. Only non-USDT assets can be held here.
          </div>
          <BalancesTable
            rows={allWallets?.coin_m?.balances || []}
            testId="coin-m-balances-table"
            emptyLabel="No coin collateral in COIN-M — Transfer BTC / ETH / BNB from Spot to use as futures collateral."
            title="COIN-M Balances"
          />
        </TabsContent>
      </Tabs>
      {/* legacy container removed — content rendered inside tabs above */}
      <div className="hidden" data-testid="balances-table-legacy"></div>

      {/* Transactions */}
      <h2 className="font-display text-white text-2xl font-semibold mb-3">Recent Transactions</h2>
      <div className="border border-[#2B3139] bg-[#181A20]">
        <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]">
          <div className="col-span-2">Type</div>
          <div className="col-span-2">Asset</div>
          <div className="col-span-3 text-right">Amount</div>
          <div className="col-span-3 text-right">Date</div>
          <div className="col-span-2 text-right">Status</div>
        </div>
        {txs.length === 0 ? (
          <div className="p-8 text-center text-[#848E9C] text-sm">No transactions yet.</div>
        ) : txs.map(t => (
          <div key={t.tx_id} className="grid grid-cols-12 px-4 py-3 border-b border-[#2B3139]/60">
            <div className="col-span-2 text-white capitalize">{t.type === 'internal_transfer' ? 'Transfer' : t.type}</div>
            <div className="col-span-2 text-[#EAECEF]">{t.symbol}</div>
            <div className="col-span-3 text-right font-mono">
              {t.amount.toFixed(4)}
              {t.from_wallet && t.to_wallet && (
                <span className="ml-2 text-[10px] uppercase text-[#848E9C]">{t.from_wallet} → {t.to_wallet}</span>
              )}
            </div>
            <div className="col-span-3 text-right text-[#848E9C] text-sm">{new Date(t.created_at).toLocaleString()}</div>
            <div className="col-span-2 text-right text-[#0ECB81] text-xs uppercase">{t.status || 'done'}</div>
          </div>
        ))}
      </div>

      {/* Whitelist address manager */}
      <Dialog open={addrOpen} onOpenChange={setAddrOpen}>
        <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm max-w-lg" data-testid="whitelist-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#0ECB81]" /> Withdrawal Address Whitelist</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {/* Add form OR email verify step */}
            {!verifyStep ? (
              <div className="border border-[#2B3139] bg-[#0B0E11] p-3 space-y-2" data-testid="whitelist-add-form">
                <div className="text-[10px] uppercase font-mono text-[#848E9C]">Add a saved address</div>
                <div className="grid grid-cols-3 gap-2">
                  <Select value={addrSymbol} onValueChange={setAddrSymbol}>
                    <SelectTrigger className="bg-[#181A20] border-[#2B3139] rounded-sm text-white" data-testid="whitelist-symbol"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#181A20] border-[#2B3139] text-white">
                      {assets.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input value={addrLabel} onChange={e => setAddrLabel(e.target.value)} placeholder="Label"
                    className="bg-[#181A20] border-[#2B3139] rounded-sm text-white col-span-2 focus-visible:ring-[#FCD535]/40 focus-visible:border-[#FCD535]"
                    data-testid="whitelist-label" />
                </div>
                <Input value={addrValue} onChange={e => setAddrValue(e.target.value)} placeholder="Address"
                  className="bg-[#181A20] border-[#2B3139] rounded-sm text-white font-mono text-xs focus-visible:ring-[#FCD535]/40 focus-visible:border-[#FCD535]"
                  data-testid="whitelist-address-input" />
                <Button onClick={addAddress} disabled={addrBusy} className="w-full bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold h-9" data-testid="whitelist-add-btn">
                  {addrBusy ? 'Sending code…' : 'Send Email Verification'}
                </Button>
                <div className="text-[10px] text-[#F0B90B] flex items-center gap-1" data-testid="whitelist-cooldown-note">
                  <ShieldCheck className="w-3 h-3" /> Email-verified + 24h cooldown before first use
                </div>
              </div>
            ) : (
              <div className="border border-[#FCD535]/40 bg-[#FCD535]/5 p-3 space-y-2" data-testid="whitelist-email-verify">
                <div className="text-[10px] uppercase font-mono text-[#FCD535]">Email verification</div>
                <div className="text-xs text-[#B7BDC6]">
                  A 6-digit code was sent to <span className="text-white font-mono">{verifyStep.email_masked}</span>. Enter it below within 10 minutes.
                </div>
                {verifyStep.mock_code && (
                  <div className="text-[10px] font-mono text-[#F0B90B] border border-[#F0B90B]/30 bg-[#F0B90B]/10 px-2 py-1" data-testid="whitelist-mock-code">
                    Dev mode: code is <span className="font-bold">{verifyStep.mock_code}</span> (email domain not verified — use this code)
                  </div>
                )}
                <Input value={verifyCode} onChange={e => setVerifyCode(e.target.value)} placeholder="Enter 6-digit code" maxLength={6}
                  className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono text-lg tracking-widest text-center focus-visible:ring-[#FCD535]/40 focus-visible:border-[#FCD535]"
                  data-testid="whitelist-verify-code-input" />
                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" onClick={cancelVerify} className="border-[#2B3139] bg-[#181A20] text-white hover:bg-[#2B3139] rounded-sm h-9" data-testid="whitelist-verify-cancel">Cancel</Button>
                  <Button onClick={verifyAddress} disabled={addrBusy || verifyCode.length < 4} className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold h-9" data-testid="whitelist-verify-btn">
                    {addrBusy ? 'Verifying…' : 'Verify & Whitelist'}
                  </Button>
                </div>
              </div>
            )}
            {/* List */}
            <div className="border border-[#2B3139] max-h-72 overflow-y-auto" data-testid="whitelist-list">
              {addresses.length === 0 ? (
                <div className="p-6 text-center text-[#848E9C] text-sm">No whitelisted addresses yet.</div>
              ) : addresses.map(a => {
                const cooldown = a.cooldown_seconds_left || 0;
                const hrs = Math.floor(cooldown / 3600);
                const mins = Math.floor((cooldown % 3600) / 60);
                return (
                <div key={a.addr_id} className="grid grid-cols-[auto_1fr_auto_auto] gap-2 items-center px-3 py-2 border-b border-[#2B3139]/60 last:border-b-0 text-xs" data-testid={`whitelist-row-${a.addr_id}`}>
                  <span className="text-[#FCD535] font-mono font-semibold w-14">{a.symbol}</span>
                  <div className="min-w-0">
                    <div className="text-white font-semibold truncate">{a.label}</div>
                    <div className="text-[10px] font-mono text-[#848E9C] truncate">{a.address}</div>
                  </div>
                  <span className={`text-[10px] uppercase font-mono px-2 py-0.5 ${a.active ? 'bg-[#0ECB81]/20 text-[#0ECB81]' : 'bg-[#F0B90B]/20 text-[#F0B90B]'}`} data-testid={`whitelist-status-${a.addr_id}`}>
                    {a.active ? 'Active' : `Cooldown ${hrs}h ${mins}m`}
                  </span>
                  <button onClick={() => removeAddress(a.addr_id)} className="p-1.5 hover:bg-[#F6465D]/20 text-[#F6465D] rounded-sm" data-testid={`whitelist-remove-${a.addr_id}`} aria-label="Remove">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transfer modal */}
      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent className="bg-[#181A20] border-[#2B3139] text-white rounded-sm" data-testid="transfer-dialog">
          <DialogHeader><DialogTitle>Transfer between wallets</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div>
                <div className="text-[10px] uppercase font-mono text-[#848E9C] mb-1">From</div>
                <Select value={tFrom} onValueChange={(v) => { setTFrom(v); if (v === tTo) setTTo(v === 'spot' ? 'futures' : 'spot'); }}>
                  <SelectTrigger className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white" data-testid="transfer-from"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#181A20] border-[#2B3139] text-white">
                    <SelectItem value="funding">Funding</SelectItem>
                    <SelectItem value="spot">Spot</SelectItem>
                    <SelectItem value="futures">Futures USDT-M</SelectItem>
                    <SelectItem value="coin_m">Futures COIN-M</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-[10px] uppercase font-mono text-[#848E9C] mb-1">To</div>
                <Select value={tTo} onValueChange={setTTo}>
                  <SelectTrigger className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white" data-testid="transfer-to"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#181A20] border-[#2B3139] text-white">
                    <SelectItem value="funding" disabled={tFrom === 'funding'}>Funding</SelectItem>
                    <SelectItem value="spot" disabled={tFrom === 'spot'}>Spot</SelectItem>
                    <SelectItem value="futures" disabled={tFrom === 'futures'}>Futures USDT-M</SelectItem>
                    <SelectItem value="coin_m" disabled={tFrom === 'coin_m'}>Futures COIN-M</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase font-mono text-[#848E9C] mb-1">Asset</div>
              <Select value={tSymbol} onValueChange={setTSymbol}>
                <SelectTrigger className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white" data-testid="transfer-symbol"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#181A20] border-[#2B3139] text-white">
                  {(tFrom === 'futures' || tTo === 'futures')
                    ? ['USDT'].map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)
                    : (tFrom === 'coin_m' || tTo === 'coin_m')
                      ? assets.filter(a => a !== 'USDT').map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)
                      : assets.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
              {(tFrom === 'futures' || tTo === 'futures') && (
                <div className="text-[10px] text-[#848E9C] mt-1">USDT-M Futures is USDT-margined only.</div>
              )}
              {(tFrom === 'coin_m' || tTo === 'coin_m') && (
                <div className="text-[10px] text-[#848E9C] mt-1">COIN-M holds crypto collateral only (no USDT).</div>
              )}
            </div>
            <div>
              <div className="text-[10px] uppercase font-mono text-[#848E9C] mb-1">Amount</div>
              <Input value={tAmount} onChange={e => setTAmount(e.target.value)} type="number" placeholder="0.00"
                className="bg-[#0B0E11] border-[#2B3139] rounded-sm text-white font-mono focus-visible:ring-[#FCD535]/40 focus-visible:border-[#FCD535]"
                data-testid="transfer-amount" />
            </div>
            <Button
              onClick={transfer}
              disabled={tBusy || !tAmount || parseFloat(tAmount) <= 0}
              className="w-full bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold"
              data-testid="confirm-transfer-btn"
            >
              <ArrowLeftRight className="w-4 h-4 mr-1.5" /> {tBusy ? 'Transferring…' : `Confirm Transfer`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Reusable balances table (matches the previous inline markup so tests keep passing)
function BalancesTable({ rows, testId, emptyLabel, title }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="border border-[#2B3139] bg-[#181A20] mt-3 p-6 text-center text-[#848E9C] text-sm" data-testid={testId}>
        {emptyLabel}
      </div>
    );
  }
  // Map symbol → CoinGecko id for /trade routing. USDT has no trade page.
  const COIN_ID_MAP = {
    BTC: 'bitcoin', ETH: 'ethereum', BNB: 'binancecoin', SOL: 'solana',
    XRP: 'ripple', ADA: 'cardano', DOGE: 'dogecoin', MATIC: 'matic-network',
    DOT: 'polkadot', TRX: 'tron', LINK: 'chainlink', LTC: 'litecoin',
    AVAX: 'avalanche-2', ATOM: 'cosmos', NEAR: 'near', SHIB: 'shiba-inu',
    UNI: 'uniswap', XLM: 'stellar', ARB: 'arbitrum', APT: 'aptos',
  };
  return (
    <div className="border border-[#2B3139] bg-[#181A20] mt-3" data-testid={testId}>
      <div className="grid grid-cols-12 px-4 py-3 text-xs uppercase tracking-wider text-[#848E9C] border-b border-[#2B3139]">
        <div className="col-span-3">{title || 'Asset'}</div>
        <div className="col-span-3 text-right">Amount</div>
        <div className="col-span-3 text-right">Price</div>
        <div className="col-span-3 text-right">USD Value</div>
      </div>
      {rows.map(b => {
        const color = coinColor(b.symbol);
        const sym = String(b.symbol || '').toUpperCase();
        const coinId = b.coin_id || COIN_ID_MAP[sym] || (sym !== 'USDT' ? sym.toLowerCase() : null);
        const clickable = !!coinId && sym !== 'USDT';
        const RowTag = clickable ? Link : 'div';
        const rowProps = clickable
          ? { to: `/trade/${coinId}`, 'data-testid': `wallet-row-${b.symbol}` }
          : { 'data-testid': `wallet-row-${b.symbol}` };
        return (
          <RowTag key={b.symbol} {...rowProps}
            className={`grid grid-cols-12 px-4 py-3 border-b border-[#2B3139]/60 items-center transition-colors ${clickable ? 'hover:bg-[#2B3139] cursor-pointer' : 'hover:bg-[#2B3139]/40'}`}>
            <div className="col-span-3 flex items-center gap-2.5 min-w-0">
              <div
                className="w-8 h-8 rounded-full grid place-items-center shrink-0 overflow-hidden"
                style={{ backgroundColor: color + '22', border: `1px solid ${color}44` }}
                data-testid={`wallet-icon-${b.symbol}`}
              >
                {b.image ? (
                  <img src={b.image} alt={b.symbol} className="w-6 h-6 rounded-full" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                ) : (
                  <span className="text-[10px] font-mono font-bold" style={{ color }}>{b.symbol?.[0]}</span>
                )}
              </div>
              <div className="min-w-0">
                <div className="text-white font-semibold text-sm leading-none">{b.symbol}</div>
                <div className="text-[10px] text-[#848E9C] truncate mt-0.5">{b.name}</div>
              </div>
            </div>
            <div className="col-span-3 text-right font-mono text-[#EAECEF]">{Number(b.amount).toFixed(6)}</div>
            <div className="col-span-3 text-right font-mono text-[#848E9C]">${b.price?.toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
            <div className="col-span-3 text-right font-mono text-white">${Number(b.usd_value).toLocaleString(undefined, { maximumFractionDigits: 2 })}</div>
          </RowTag>
        );
      })}
    </div>
  );
}

// 30-day portfolio equity curve — powers Overview tab
function PnlHistoryChart({ pnl }) {
  // Hooks must be called before any early return
  const [showBtc, setShowBtc] = useState(true);
  const [showEth, setShowEth] = useState(true);
  const [showCompare, setShowCompare] = useState(true);
  if (!pnl || !pnl.series || pnl.series.length < 2) {
    return (
      <div className="border border-[#2B3139] bg-[#181A20] p-6 mt-3 text-center text-[#848E9C] text-sm" data-testid="pnl-chart-empty">
        Portfolio history will appear after your first daily snapshot.
      </div>
    );
  }
  const vals = pnl.series.map(s => Number(s.value_usd) || 0);
  const btcVals = (pnl.btc_series || []).map(s => Number(s.value_usd) || 0);
  const ethVals = (pnl.eth_series || []).map(s => Number(s.value_usd) || 0);
  const cmpVals = (pnl.compare_series || []).map(s => Number(s.value_usd) || 0);
  const cmpMeta = pnl.compare_meta;
  const overlaySets = [];
  if (showBtc && btcVals.length) overlaySets.push(btcVals);
  if (showEth && ethVals.length) overlaySets.push(ethVals);
  if (showCompare && cmpVals.length) overlaySets.push(cmpVals);
  const combined = [...vals, ...overlaySets.flat()];
  const minV = Math.min(...combined);
  const maxV = Math.max(...combined);
  const pad = 6;
  const W = 900, H = 200;
  const range = Math.max(1e-6, maxV - minV);
  const stepX = (W - pad * 2) / Math.max(1, vals.length - 1);
  const y = v => pad + (H - pad * 2) - ((v - minV) / range) * (H - pad * 2);
  const points = vals.map((v, i) => `${pad + i * stepX},${y(v)}`).join(' ');
  const btcStepX = btcVals.length > 1 ? (W - pad * 2) / (btcVals.length - 1) : 0;
  const btcPoints = btcVals.map((v, i) => `${pad + i * btcStepX},${y(v)}`).join(' ');
  const ethStepX = ethVals.length > 1 ? (W - pad * 2) / (ethVals.length - 1) : 0;
  const ethPoints = ethVals.map((v, i) => `${pad + i * ethStepX},${y(v)}`).join(' ');
  const cmpStepX = cmpVals.length > 1 ? (W - pad * 2) / (cmpVals.length - 1) : 0;
  const cmpPoints = cmpVals.map((v, i) => `${pad + i * cmpStepX},${y(v)}`).join(' ');
  const up = (pnl.pnl_usd || 0) >= 0;
  const color = up ? '#0ECB81' : '#F6465D';
  const last = vals[vals.length - 1];
  const first = vals[0];
  const vsBtc = pnl.vs_btc_pct ?? null;
  const vsEth = pnl.vs_eth_pct ?? null;
  const vsCmp = pnl.vs_compare_pct ?? null;
  return (
    <div className="border border-[#2B3139] bg-[#181A20] mt-3" data-testid="pnl-chart-card">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#2B3139] gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase font-mono text-[#848E9C]">Portfolio · {pnl.days}d equity curve</div>
          <div className="text-white font-mono text-2xl mt-0.5" data-testid="pnl-chart-current">
            ${last.toLocaleString(undefined, { maximumFractionDigits: 2 })}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {btcVals.length > 0 && (
            <button
              onClick={() => setShowBtc(v => !v)}
              className={`text-[10px] uppercase font-mono px-2 py-1 border ${showBtc ? 'text-[#F0B90B] border-[#F0B90B]' : 'text-[#5E6673] border-[#2B3139]'} rounded-sm`}
              data-testid="pnl-btc-toggle"
              aria-pressed={showBtc}
            >
              <span className="inline-block w-2 h-2 mr-1" style={{ backgroundColor: showBtc ? '#F0B90B' : '#5E6673' }} />
              BTC-hold
            </button>
          )}
          {ethVals.length > 0 && (
            <button
              onClick={() => setShowEth(v => !v)}
              className={`text-[10px] uppercase font-mono px-2 py-1 border ${showEth ? 'text-[#627EEA] border-[#627EEA]' : 'text-[#5E6673] border-[#2B3139]'} rounded-sm`}
              data-testid="pnl-eth-toggle"
              aria-pressed={showEth}
            >
              <span className="inline-block w-2 h-2 mr-1" style={{ backgroundColor: showEth ? '#627EEA' : '#5E6673' }} />
              ETH-hold
            </button>
          )}
          {cmpVals.length > 0 && cmpMeta && (
            <button
              onClick={() => setShowCompare(v => !v)}
              className={`text-[10px] uppercase font-mono px-2 py-1 border ${showCompare ? 'text-[#FCD535] border-[#FCD535]' : 'text-[#5E6673] border-[#2B3139]'} rounded-sm`}
              data-testid="pnl-compare-toggle"
              aria-pressed={showCompare}
            >
              <span className="inline-block w-2 h-2 mr-1" style={{ backgroundColor: showCompare ? '#FCD535' : '#5E6673' }} />
              {cmpMeta.label}-hold
            </button>
          )}
          <div className="text-right">
            <div className={`font-mono text-sm ${up ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`} data-testid="pnl-chart-pct">
              {up ? '+' : ''}${pnl.pnl_usd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
            </div>
            <div className={`font-mono text-xs ${up ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`} data-testid="pnl-chart-pnl">
              {up ? '+' : ''}{pnl.pnl_pct}%
            </div>
            {btcVals.length > 0 && vsBtc !== null && (
              <div className={`font-mono text-[10px] mt-1 ${vsBtc >= 0 ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`} data-testid="pnl-vs-btc">
                {vsBtc >= 0 ? 'Beating' : 'Trailing'} BTC by {vsBtc >= 0 ? '+' : ''}{vsBtc}%
              </div>
            )}
            {ethVals.length > 0 && vsEth !== null && (
              <div className={`font-mono text-[10px] ${vsEth >= 0 ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`} data-testid="pnl-vs-eth">
                {vsEth >= 0 ? 'Beating' : 'Trailing'} ETH by {vsEth >= 0 ? '+' : ''}{vsEth}%
              </div>
            )}
            {cmpVals.length > 0 && vsCmp !== null && cmpMeta && (
              <div className={`font-mono text-[10px] ${vsCmp >= 0 ? 'text-[#0ECB81]' : 'text-[#F6465D]'}`} data-testid="pnl-vs-compare">
                {vsCmp >= 0 ? 'Beating' : 'Trailing'} {cmpMeta.label} by {vsCmp >= 0 ? '+' : ''}{vsCmp}%
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="p-3">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 220 }} preserveAspectRatio="none" data-testid="pnl-chart-svg">
          <line x1={pad} x2={W - pad} y1={y(minV)} y2={y(minV)} stroke="#2B3139" strokeDasharray="2,3" strokeWidth="0.5" />
          <line x1={pad} x2={W - pad} y1={y(maxV)} y2={y(maxV)} stroke="#2B3139" strokeDasharray="2,3" strokeWidth="0.5" />
          <line x1={pad} x2={W - pad} y1={y(first)} y2={y(first)} stroke="#FCD535" strokeDasharray="1,4" strokeWidth="0.4" opacity="0.6" />
          <polygon
            fill={color + '22'}
            points={`${pad},${y(minV)} ${points} ${pad + (vals.length - 1) * stepX},${y(minV)}`}
          />
          <polyline fill="none" stroke={color} strokeWidth="1.5" points={points} />
          {showBtc && btcVals.length > 1 && (
            <polyline fill="none" stroke="#F0B90B" strokeWidth="1.2" strokeDasharray="4,3" points={btcPoints} data-testid="pnl-btc-line" />
          )}
          {showEth && ethVals.length > 1 && (
            <polyline fill="none" stroke="#627EEA" strokeWidth="1.2" strokeDasharray="4,3" points={ethPoints} data-testid="pnl-eth-line" />
          )}
          {showCompare && cmpVals.length > 1 && (
            <polyline fill="none" stroke="#FCD535" strokeWidth="1.2" strokeDasharray="2,3" points={cmpPoints} data-testid="pnl-compare-line" />
          )}
          <circle cx={pad + (vals.length - 1) * stepX} cy={y(last)} r="3" fill={color} />
          <text x={W - pad} y={y(maxV) - 4} fontSize="10" fontFamily="monospace" fill="#848E9C" textAnchor="end">${maxV.toLocaleString(undefined, { maximumFractionDigits: 0 })}</text>
          <text x={W - pad} y={y(minV) + 12} fontSize="10" fontFamily="monospace" fill="#848E9C" textAnchor="end">${minV.toLocaleString(undefined, { maximumFractionDigits: 0 })}</text>
        </svg>
        <div className="flex items-center justify-between mt-1 text-[10px] font-mono text-[#5E6673]">
          <span>{pnl.series[0]?.day}</span>
          <span>Today</span>
        </div>
      </div>
    </div>
  );
}



// Anti-phishing phrase editor — appears in every convert/withdrawal email
function AntiPhishingRow({ current, onSave }) {
  const [val, setVal] = useState(current);
  const [editing, setEditing] = useState(false);
  useEffect(() => { setVal(current); }, [current]);
  const dirty = val.trim() !== (current || '').trim();
  return (
    <div className="border border-[#2B3139] bg-[#181A20] px-5 py-3 mb-6 flex items-center gap-3 flex-wrap" data-testid="anti-phishing-card">
      <Fingerprint className={`w-4 h-4 ${current ? 'text-[#0ECB81]' : 'text-[#5E6673]'}`} />
      <div className="flex-1 min-w-[200px]">
        <div className="text-white text-sm font-semibold">Anti-phishing phrase</div>
        <div className="text-[11px] text-[#848E9C]">A short code shown at the top of every Convert / Withdrawal email. If you don't see it, the message is fake.</div>
      </div>
      {editing ? (
        <>
          <input
            value={val}
            onChange={e => setVal(e.target.value.slice(0, 32))}
            placeholder="e.g. blue-panda-42"
            maxLength={32}
            className="bg-[#0B0E11] border border-[#2B3139] focus:border-[#FCD535] outline-none px-3 py-1.5 rounded-sm text-white font-mono text-sm w-56"
            data-testid="anti-phishing-input"
            autoFocus
          />
          <button
            onClick={async () => { await onSave(val.trim()); setEditing(false); }}
            disabled={!dirty}
            className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold px-3 py-1.5 text-xs uppercase disabled:opacity-40"
            data-testid="anti-phishing-save"
          >Save</button>
          <button
            onClick={() => { setVal(current); setEditing(false); }}
            className="text-[#848E9C] hover:text-white text-xs uppercase px-2"
            data-testid="anti-phishing-cancel"
          >Cancel</button>
        </>
      ) : (
        <>
          <span className="font-mono text-sm px-2 py-1 border border-[#2B3139] bg-[#0B0E11] text-[#0ECB81]" data-testid="anti-phishing-current">
            {current || <span className="text-[#5E6673] italic">Not set</span>}
          </span>
          <button
            onClick={() => setEditing(true)}
            className="text-[#FCD535] hover:text-white text-xs uppercase font-mono px-2"
            data-testid="anti-phishing-edit"
          >{current ? 'Change' : 'Set phrase'}</button>
          {current && (
            <button
              onClick={() => onSave('')}
              className="text-[#F6465D] hover:text-white text-xs uppercase font-mono px-2"
              data-testid="anti-phishing-clear"
            >Clear</button>
          )}
        </>
      )}
    </div>
  );
}



// Compact user-agent parser — surface OS + browser like Binance's device list
function describeUA(ua) {
  if (!ua || ua === 'unknown') return 'Unknown device';
  const s = ua.toLowerCase();
  let os = 'Unknown OS';
  if (s.includes('windows')) os = 'Windows';
  else if (s.includes('mac os') || s.includes('macintosh')) os = 'macOS';
  else if (s.includes('android')) os = 'Android';
  else if (s.includes('iphone') || s.includes('ipad') || s.includes('ios')) os = 'iOS';
  else if (s.includes('linux')) os = 'Linux';
  let browser = 'Browser';
  if (s.includes('edg/')) browser = 'Edge';
  else if (s.includes('chrome/') && !s.includes('edg/')) browser = 'Chrome';
  else if (s.includes('firefox/')) browser = 'Firefox';
  else if (s.includes('safari/') && !s.includes('chrome/')) browser = 'Safari';
  else if (s.includes('curl')) browser = 'curl';
  else if (s.includes('httpx') || s.includes('python')) browser = 'API client';
  return `${browser} · ${os}`;
}

// Security & Devices card — 2FA toggle + active sessions list
function SecurityDevicesCard({ twoFa, sessions, onSetup, onDisable, onRevoke }) {
  const enabled = !!twoFa?.enabled;
  return (
    <div className="border border-[#2B3139] bg-[#181A20] mb-6" data-testid="security-devices-card">
      <div className="px-5 py-3 border-b border-[#2B3139] flex items-center gap-3 flex-wrap">
        <ShieldCheck className={`w-4 h-4 ${enabled ? 'text-[#0ECB81]' : 'text-[#F0B90B]'}`} />
        <div className="flex-1 min-w-[220px]">
          <div className="text-white text-sm font-semibold">
            2FA (Google Authenticator) · {enabled ? <span className="text-[#0ECB81]">ON</span> : <span className="text-[#F0B90B]">OFF — mandatory for withdrawals</span>}
          </div>
          <div className="text-[11px] text-[#848E9C]">
            Every withdrawal requires a fresh 6-digit code from your authenticator app.
          </div>
        </div>
        {!enabled ? (
          <button
            onClick={onSetup}
            className="bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm font-semibold px-3 py-1.5 text-xs uppercase"
            data-testid="twofa-setup-btn"
          >Enable 2FA</button>
        ) : (
          <button
            onClick={onDisable}
            className="border border-[#F6465D] text-[#F6465D] hover:bg-[#F6465D]/10 rounded-sm font-semibold px-3 py-1.5 text-xs uppercase"
            data-testid="twofa-disable-open-btn"
          >Disable</button>
        )}
      </div>
      <div className="px-5 py-3">
        <div className="flex items-center gap-2 mb-2">
          <Fingerprint className="w-3.5 h-3.5 text-[#848E9C]" />
          <div className="text-xs uppercase font-mono text-[#848E9C]">Active Sessions</div>
          <div className="text-[10px] font-mono text-[#5E6673] ml-1">({sessions.length})</div>
        </div>
        {sessions.length === 0 ? (
          <div className="text-[#848E9C] text-xs py-4 text-center">No active sessions.</div>
        ) : (
          <div className="border border-[#2B3139] divide-y divide-[#2B3139]/60" data-testid="sessions-list">
            {sessions.map(s => (
              <div key={s.session_id} className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-3 px-3 py-2.5 text-xs" data-testid={`session-row-${s.session_id.slice(-6)}`}>
                <span className={`text-[10px] uppercase font-mono px-2 py-0.5 rounded-sm ${s.provider === 'google' ? 'bg-[#4285F4]/20 text-[#4285F4]' : 'bg-[#FCD535]/20 text-[#FCD535]'}`}>
                  {s.provider === 'google' ? 'Google' : 'JWT'}
                </span>
                <div className="min-w-0">
                  <div className="text-white font-semibold truncate">{describeUA(s.user_agent)}</div>
                  <div className="text-[10px] font-mono text-[#848E9C] truncate">
                    {s.ip} · signed in {s.created_at ? new Date(s.created_at).toLocaleString() : '—'}
                  </div>
                </div>
                {s.current ? (
                  <span className="text-[10px] uppercase font-mono px-2 py-0.5 bg-[#0ECB81]/20 text-[#0ECB81] rounded-sm" data-testid={`session-current-${s.session_id.slice(-6)}`}>
                    This device
                  </span>
                ) : <span />}
                {s.current ? (
                  <span className="text-[10px] uppercase text-[#5E6673] font-mono px-1">—</span>
                ) : (
                  <button
                    onClick={() => onRevoke(s.session_id)}
                    className="text-[#F6465D] hover:text-white text-xs uppercase font-mono px-2"
                    data-testid={`session-revoke-${s.session_id.slice(-6)}`}
                  >Revoke</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
